/**
 * The /diagnostics doctor page (web-client.md §8). A self-contained controller reachable at the hash
 * route `#/diagnostics` with NO auth required for the connectivity half. It builds its own REST +
 * WS clients against the resolved server URL, runs the {@link core/diagnostics} checks against real
 * browser probes, renders pass/warn/fail into the terminal, and can export a sanitized bundle or
 * repair (clear) the local caches.
 *
 * This is DOM/browser glue (app layer). The check + redaction logic it calls is DOM-free and unit
 * tested in `core/diagnostics`.
 */
import {
  AuthClient,
  CommandLog,
  HttpClient,
  PROTOCOL_VERSION,
  Store,
  VersionClient,
  apiCheck,
  authCheck,
  buildDiagnosticBundle,
  capabilityCheck,
  clockSkewCheck,
  decideUpdate,
  flagsCheck,
  openSessionCache,
  rawWebSocketTransportFactory,
  serializeBundle,
  storageCheck,
  versionCheck,
  wsHandshakeCheck,
  WsSession,
} from '../core/index.ts';
import type { Capabilities, CheckResult, VersionManifest } from '../core/index.ts';
import { Terminal } from '../ui/term/index.ts';
import { renderDiagnostics } from '../ui/views/diagnosticsView.ts';
import { SettingsStore } from '../core/index.ts';
import { resolveConfig } from './config.ts';
import type { AppConfig } from './config.ts';
import { browserKeyValueStore, gatherCapabilities, storageProbe } from './browserEnv.ts';
import { t } from '../ui/i18n.ts';

const WS_PROBE_TIMEOUT_MS = 8_000;

export class DiagnosticsPage {
  private readonly term: Terminal;
  private readonly config: AppConfig;
  private readonly http: HttpClient;
  private readonly versions: VersionClient;
  private readonly auth: AuthClient;
  private readonly commandLog = new CommandLog();

  private results: CheckResult[] = [];
  private running = false;
  private logLines: string[] = [];
  private caps: Capabilities | null = null;
  private lastConnection = 'idle';

  constructor(mount: HTMLElement, language: string) {
    const settings = new SettingsStore(browserKeyValueStore());
    this.config = resolveConfig(settings, language);
    this.http = new HttpClient({
      baseUrl: this.config.serverUrl,
      clientVersion: this.config.clientVersion,
      language: this.config.language,
    });
    this.versions = new VersionClient(this.http);
    this.auth = new AuthClient(this.http);

    this.term = new Terminal(mount);
    this.term.setPrompt('doctor>');
    this.term.onSubmit((text) => void this.onInput(text));
  }

  start(): void {
    this.render();
    this.term.focus();
    void this.run();
  }

  private async onInput(raw: string): Promise<void> {
    const cmd = raw.trim().toLowerCase();
    if (!cmd) return;
    switch (cmd) {
      case 'run':
        await this.run();
        break;
      case 'repair':
        await this.repair();
        break;
      case 'bundle':
        this.downloadBundle();
        break;
      case 'back':
      case 'exit':
        globalThis.location.hash = '';
        globalThis.location.reload();
        break;
      default:
        this.print(t('diag.unknown', { cmd }));
    }
  }

  /** Run every check in sequence, rendering as each resolves. */
  private async run(): Promise<void> {
    this.running = true;
    this.results = [];
    this.render();

    this.caps = await gatherCapabilities();
    this.push(capabilityCheck(this.caps));

    // version + protocol + update decision (from the manifest).
    let manifest: VersionManifest | null = null;
    try {
      manifest = await this.versions.manifest();
      this.push(
        versionCheck({
          clientVersion: this.config.clientVersion,
          clientProtocol: PROTOCOL_VERSION,
          manifest,
        }),
      );
      const decision = decideUpdate({
        clientVersion: this.config.clientVersion,
        clientProtocol: PROTOCOL_VERSION,
        manifest,
      });
      if (decision.action !== 'none') {
        this.print(t('diag.update', { action: decision.action, reason: decision.reason }));
      }
    } catch (error) {
      this.push({
        id: 'version',
        label: 'Version & protocol',
        status: 'fail',
        detail: `manifest unreachable — ${msg(error)}`,
      });
    }

    // API reachability + latency (GET /time).
    this.push(await apiCheck(() => this.versions.time().then((r) => r.latencyMs)));

    // Clock skew (client vs server /time, corrected for half the round-trip).
    this.push(await this.clockCheck());

    // Storage integrity.
    this.push(await storageCheck(storageProbe));

    // Auth/token state (skips cleanly when not signed in — this is the no-auth half).
    this.push(authCheck({ authenticated: false, expiresInMs: null, playerIdPresent: false }));

    // Effective flags — not loaded without a live session on this standalone page.
    this.push(flagsCheck({}));

    // WS handshake round-trip.
    this.push(await wsHandshakeCheck(() => this.wsProbe()));

    this.running = false;
    this.render();
  }

  private async clockCheck(): Promise<CheckResult> {
    try {
      const { body, latencyMs } = await this.versions.time();
      const serverMs = Date.parse(body.serverTime);
      // Approximate the one-way offset by removing half the measured round-trip.
      const skewMs = Date.now() - latencyMs / 2 - serverMs;
      return clockSkewCheck({ skewMs });
    } catch (error) {
      return {
        id: 'clock',
        label: 'Clock skew',
        status: 'fail',
        detail: `no server time — ${msg(error)}`,
      };
    }
  }

  /** Open a socket, handshake, and measure ms to sys.welcome, then close. Needs a token → guest login. */
  private async wsProbe(): Promise<number> {
    const guest = await this.auth.guest().catch((e: unknown) => {
      throw new Error(`guest login failed: ${msg(e)}`);
    });
    const store = new Store();
    const started = Date.now();
    return new Promise<number>((resolve, reject) => {
      let done = false;
      const finish = (fn: () => void): void => {
        if (done) return;
        done = true;
        clearTimeout(timer);
        unsubscribe();
        ws.stop('diagnostics');
        fn();
      };
      const ws = new WsSession({
        url: this.config.wsUrl,
        transportFactory: rawWebSocketTransportFactory,
        store,
        tokenProvider: () => guest.access,
        clientVersion: this.config.clientVersion,
        platform: this.config.platform,
        language: this.config.language,
        onAuthError: (e) => finish(() => reject(new Error(e.code))),
      });
      const unsubscribe = store.subscribe(() => {
        this.lastConnection = store.getConnection().status;
        if (store.getConnection().status === 'live') {
          const rtt = Date.now() - started;
          finish(() => resolve(rtt));
        }
      });
      const timer = globalThis.setTimeout(
        () => finish(() => reject(new Error('handshake timeout'))),
        WS_PROBE_TIMEOUT_MS,
      );
      ws.start();
    });
  }

  private async repair(): Promise<void> {
    try {
      const cache = await openSessionCache();
      await cache.clear();
      this.print(t('diag.cachesCleared'));
    } catch (error) {
      this.print(t('diag.repairFailed', { error: msg(error) }));
    }
  }

  private downloadBundle(): void {
    const bundle = buildDiagnosticBundle({
      clientVersion: this.config.clientVersion,
      protocolVersion: PROTOCOL_VERSION,
      serverUrl: this.config.serverUrl,
      flags: {},
      connection: { status: this.lastConnection, attempt: 0, lastError: null },
      commands: this.commandLog.entries(),
      logTail: this.logLines,
      checks: this.results,
      capabilities: (this.caps as unknown as Record<string, unknown>) ?? {},
      generatedAt: new Date().toISOString(),
    });
    const json = serializeBundle(bundle);
    try {
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `teamsim-diagnostics-${Date.now()}.json`;
      a.click();
      URL.revokeObjectURL(url);
      this.print(t('diag.bundleDownloaded'));
    } catch {
      // Download API unavailable (e.g. headless): print a short confirmation instead.
      this.print(t('diag.bundleReady', { bytes: json.length }));
    }
  }

  private push(result: CheckResult): void {
    this.results.push(result);
    this.render();
  }

  private print(line: string): void {
    this.logLines.push(line);
    if (this.logLines.length > 50) this.logLines = this.logLines.slice(-50);
    this.render();
  }

  private render(): void {
    this.term.render(
      renderDiagnostics({
        serverUrl: this.config.serverUrl,
        running: this.running,
        results: this.results,
        log: this.logLines,
      }),
    );
  }
}

function msg(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
